import { FC, memo, useCallback, useMemo, useState } from 'react'
import { PaymentElement } from '@stripe/react-stripe-js'
import { Controller } from 'react-hook-form'
import monthlyCreditLimitForm from '../../../../forms/monthlyCreditLimitForm'
import { useSubscription } from '../../../providers/SubscriptionProvider'
import config from '../../../../config'
import { StripeStyleCounterInput } from '../../../atoms/StripeStyleCounterInput'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import AddCardIcon from '@mui/icons-material/AddCard'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import { ProcessingPaymentPanel } from '../ProcessingPaymentPanel'
import { AdditionalTopupCard } from '../AdditionalTopupCard'
import { CompanyCardsPanel } from '../CompanyCardsPanel'
import { useCompanyCards } from '../../../providers/CompanyCardsProvider'
import { CompanyCardRow } from '../CompanyCardRow'
import LinearProgress from '../../../atoms/LinearProgress'
import { CompanyCardMenu } from '../CompanyCardMenu'
import ButtonBase from '@mui/material/ButtonBase'
import Radio from '@mui/material/Radio'
import { useCompanyPaymentsPreview } from '../../../providers/CompanyPaymentsProvider'
import { CompanyPaymentsPanel } from '../CompanyPaymentsPanel'
import useCallbackDebounced from '../../../../hooks/shared/useCallbackDebounced'
import { useNavigate } from 'react-router-dom'
import { routes } from '../../../../routes'
import { currencyFormatter } from '../../../../formatters/currencyFormatter'
import pricingCalculator from '../../../../calculators/pricingCalculator'
import { PromoBox } from '../PromoBox'
import { PromotionalCode } from '@leadgen/models/client'

const getPromotionalCodeBenefitsMessage = ({
  promotionalCode,
  promotionalCredits,
  monthlyCreditLimit,
}: {
  promotionalCode: PromotionalCode
  promotionalCredits: number
  monthlyCreditLimit: number
}) => {
  switch (promotionalCode.type) {
    case 'percentage-additional-credits': {
      if (promotionalCredits === 0) {
        return ` You will receive free credits on your first month when you subscribe for a minimum of ${Math.ceil(100 / promotionalCode.value)}.`
      }
      return ` You will receive ${promotionalCredits} free credit${
        promotionalCredits === 1 ? '' : 's'
      } on your first month if you subscribe now for ${monthlyCreditLimit} credits.`
    }
    default: {
      return ''
    }
  }
}

const SubscriptionPanel: FC = () => {
  const navigate = useNavigate()
  const { companyCards, status: companyCardsStatus } = useCompanyCards()
  const { companyPayments, status: companyPaymentsStatus } = useCompanyPaymentsPreview()
  const {
    creditsPurchasedThisMonth,
    canUsePromotionalCode,
    onHandleSubmitSubscribe,
    onHandleSubmitUnsubscribe,
    monthlyCreditLimitControl,
    promoCodeControl,
    subscribeControl,
    availableTopupCredits,
    state,
    subscribeDisabled,
    processing,
    unsubscribeDisabled,
    monthlyCreditLimitDisabled,
    topupError,
    subscribeError,
    unsubscribeError,
    monthlyCreditLimitError,
    monthlyCreditLimitSubmitting,
    subscribeSubmitting,
    unsubscribeSubmitting,
    monthlyCreditLimitInForm,
    handlePaymentElementChange,
    continueOnPaymentComplete,
    credits,
    appliedPromotionalCode,
    promoCodeSubmitting,
    promotionalCodeBootstrapping,
    onApplyPromoCode,
    onClearAppliedPromoCode,
  } = useSubscription({
    companyCards,
  })
  const { amountToPayInPounds: remainingAmountToPay, promotionalCredits } = useMemo(() => {
    const amountOffset = Math.max(credits || 0, 0)
    const numberOfCreditsToBuy = Math.max((monthlyCreditLimitInForm || 0) - amountOffset, 0)
    if (numberOfCreditsToBuy === 0) {
      return { amountToPayInPounds: null, promotionalCredits: null }
    }
    const { amountToPay, creditsBreakdown } =
      pricingCalculator.calculateCreditsBreakdownForPurchase({
        numberOfCreditsToBuy,
        alreadyPurchasedThisMonth: creditsPurchasedThisMonth ?? 0,
        promotionalCode: appliedPromotionalCode || undefined,
      })
    const amountToPayInPence = Math.max(amountToPay, 0)
    const amountToPayInPounds = currencyFormatter.formatPenceToGBPAmountString(amountToPayInPence)
    const promotionalCredits = creditsBreakdown?.promotionalCredits || null
    return { amountToPayInPounds, promotionalCredits }
  }, [appliedPromotionalCode, credits, creditsPurchasedThisMonth, monthlyCreditLimitInForm])
  const totalCostOfSubscription = useMemo(() => {
    const { amountToPay } = pricingCalculator.calculateCreditsBreakdownForPurchase({
      numberOfCreditsToBuy: monthlyCreditLimitInForm || 0,
      alreadyPurchasedThisMonth: 0,
    })
    const totalCostOfSubscriptionInPence = Math.max(amountToPay, 0)
    if (totalCostOfSubscriptionInPence === 0) {
      return null
    }
    const totalCostOfSubscriptionInPounds = currencyFormatter.formatPenceToGBPAmountString(
      totalCostOfSubscriptionInPence,
    )
    return totalCostOfSubscriptionInPounds
  }, [monthlyCreditLimitInForm])
  const [confirmUnsubscribeDialogOpen, setConfirmUnsubscribeDialogOpen] = useState(false)
  const openConfirmUnsubscribeCardDialog = useCallback(() => {
    setConfirmUnsubscribeDialogOpen(true)
  }, [])
  const closeConfirmUnsubscribeCardDialog = useCallback(() => {
    setConfirmUnsubscribeDialogOpen(false)
  }, [])
  const wrappedOnHandleSubmitUnsubscribe = useCallback(
    (e?: React.BaseSyntheticEvent) => {
      setConfirmUnsubscribeDialogOpen(false)
      onHandleSubmitUnsubscribe(e)
    },
    [onHandleSubmitUnsubscribe],
  )
  const handleViewAllPaymentsClick = useCallbackDebounced(() => {
    navigate(routes.companyPayments)
  }, [navigate])

  const error = topupError || subscribeError || unsubscribeError || monthlyCreditLimitError
  if (
    state === 'PROCESSING_PAYMENT' ||
    state === 'PROCESSING_SUBSCRIPTION' ||
    state === 'PAYMENT_COMPLETE' ||
    state === 'SUBSCRIPTION_COMPLETE'
  ) {
    return (
      <ProcessingPaymentPanel state={state} continueOnPaymentComplete={continueOnPaymentComplete} />
    )
  }
  return (
    <Box
      id="subscription-panel"
      sx={theme => ({
        overflow: 'scroll',
        width: '100%',
        padding: 2,
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 2,
        [theme.breakpoints.down('sm')]: {
          paddingTop: '70px',
          paddingBottom: '70px',
        },
      })}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: { xs: 0, md: 1 },
        }}
      >
        <Box
          sx={{
            marginBottom: 4,
          }}
        >
          <Typography variant="h6" component={'span'}>
            {state === 'UNSUBSCRIBED'
              ? '1. Configure your monthly credit limit'
              : 'Manage your credit monthly limit'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
          <Controller
            name="monthlyCreditLimit"
            control={monthlyCreditLimitControl}
            render={({ field, fieldState }) => (
              <StripeStyleCounterInput
                {...field}
                helperText={fieldState.error?.message || ' '}
                error={!!fieldState.error?.message}
                label="Monthly credit limit"
                disabled={monthlyCreditLimitDisabled}
                loading={monthlyCreditLimitSubmitting}
              />
            )}
            rules={{
              validate: monthlyCreditLimitForm.validateMonthlyCreditLimitField,
            }}
          />
          {state === 'SUBSCRIBED' && (
            <Typography sx={{ marginTop: '12px' }} variant="subtitle2">
              {totalCostOfSubscription} per month max
            </Typography>
          )}
        </Box>
        {state === 'SUBSCRIBED' && (
          <>
            <Box sx={{ display: 'flex', marginLeft: '-2px', marginTop: 2, marginBottom: 0.5 }}>
              <InfoOutlinedIcon sx={{ fontSize: '26px' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 0.5 }}>
              <Typography variant="body2">
                You can manage your monthly credit limit here. Each credit equals one letter sent.
              </Typography>
              <Typography variant="body2">
                Any unspent credits roll over each month, and we will top up your credits up to your
                limit at the start of each month. You only pay for what you use.
              </Typography>
              <Typography variant="body2">You can cancel any time.</Typography>
            </Box>
            <Box sx={{ marginTop: 2 }}>{!!availableTopupCredits && <AdditionalTopupCard />}</Box>
          </>
        )}
        {state === 'UNSUBSCRIBED' && (
          <>
            <Box sx={{ display: 'flex', marginLeft: '-2px', marginTop: 2, marginBottom: 0.5 }}>
              <InfoOutlinedIcon sx={{ fontSize: '26px' }} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2">
                Start by configuring your maximum monthly credit limit. Each credit equals one
                letter sent to a prospective customer.
              </Typography>
              <Typography variant="body2">
                Any unspent credits roll over each month, and we will top up your credits up to your
                limit at the start of each month. You only pay for what you use.
              </Typography>
              <Typography variant="body2">You can cancel any time.</Typography>
            </Box>
          </>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
        }}
      >
        {state === 'SUBSCRIBED' && (
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                marginBottom: 4,
              }}
            >
              <Typography
                variant="h6"
                component={'div'}
                sx={{ width: 'fit-content', position: 'relative' }}
              >
                Payment methods
                {companyCardsStatus === 'loading' && (
                  <Box sx={{ position: 'absolute', bottom: -2, left: 0, right: 0 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Typography>
            </Box>
            <Box sx={{ marginBottom: 4 }}>
              <CompanyCardsPanel />
            </Box>
            <Box
              sx={{
                marginBottom: 4,
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="h6"
                component={'div'}
                sx={{ width: 'fit-content', position: 'relative' }}
              >
                Latest payments
                {companyPaymentsStatus === 'loading-initial' && (
                  <Box sx={{ position: 'absolute', bottom: -2, left: 0, right: 0 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Typography>
              {companyPayments.length === 3 && (
                <Button onClick={handleViewAllPaymentsClick}>view all</Button>
              )}
            </Box>
            <Box sx={{ marginBottom: 4 }}>
              <CompanyPaymentsPanel />
            </Box>
            <Box
              sx={{
                flex: 1,
              }}
            />
            <Button
              variant="contained"
              onClick={openConfirmUnsubscribeCardDialog}
              disabled={unsubscribeDisabled}
              loading={unsubscribeSubmitting}
              loadingPosition="end"
              color="error"
            >
              cancel subscription
            </Button>
            <Dialog open={confirmUnsubscribeDialogOpen} onClose={closeConfirmUnsubscribeCardDialog}>
              <DialogTitle>Cancel subscription?</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  Are you sure you want to cancel your subscription?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeConfirmUnsubscribeCardDialog}>No</Button>
                <Button onClick={wrappedOnHandleSubmitUnsubscribe} autoFocus>
                  Yes
                </Button>
              </DialogActions>
            </Dialog>
            {error && (
              <Box sx={{ marginTop: 1 }}>
                <Typography variant="body2" color={'error'}>
                  {error}
                </Typography>
              </Box>
            )}
          </Box>
        )}
        {state === 'UNSUBSCRIBED' && (
          <>
            <Box
              sx={{
                marginBottom: 4,
              }}
            >
              <Typography
                variant="h6"
                component={'div'}
                sx={{ width: 'fit-content', position: 'relative' }}
              >
                2. Provide payment details{' '}
                {companyCardsStatus === 'loading' && (
                  <Box sx={{ position: 'absolute', bottom: -2, left: 0, right: 0 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Typography>
            </Box>
            {canUsePromotionalCode && (
              <Box sx={{ marginBottom: 1.5 }}>
                <PromoBox
                  control={promoCodeControl}
                  appliedPromotionalCode={appliedPromotionalCode}
                  promoCodeSubmitting={promoCodeSubmitting}
                  promotionalCodeBootstrapping={promotionalCodeBootstrapping}
                  disabled={processing}
                  onApplyPromoCode={onApplyPromoCode}
                  onClearAppliedPromoCode={onClearAppliedPromoCode}
                />
              </Box>
            )}
            <Controller
              name="paymentMethodId"
              control={subscribeControl}
              render={({ field }) => {
                if (
                  companyCardsStatus === 'loading' ||
                  companyPaymentsStatus === 'loading-initial'
                ) {
                  return <></>
                }
                if (!companyCards.length) {
                  return (
                    <PaymentElement
                      options={{
                        readOnly: subscribeSubmitting,
                      }}
                      onChange={handlePaymentElementChange}
                    />
                  )
                }
                return (
                  <Box
                    sx={{
                      display: 'flex',
                      flex: 1,
                      flexDirection: 'column',
                      marginBottom: 2,
                      gap: 2,
                    }}
                  >
                    <Typography variant="body2">Choose a saved card</Typography>
                    {companyCards.map(companyCard => {
                      return (
                        <CompanyCardRow
                          key={companyCard.id}
                          companyCard={companyCard}
                          selectable
                          isSelected={companyCard.id === field.value}
                          onClick={companyCardId => field.onChange(companyCardId)}
                          disabled={processing}
                          menuElement={<CompanyCardMenu companyCard={companyCard} enableDelete />}
                        />
                      )
                    })}
                    {field.value === null ? (
                      <PaymentElement
                        options={{
                          readOnly: subscribeSubmitting,
                        }}
                        onChange={handlePaymentElementChange}
                      />
                    ) : (
                      <>
                        <Typography variant="body2">or</Typography>
                        <ButtonBase
                          component="div"
                          onClick={() => field.onChange(null)}
                          sx={{
                            display: 'flex',
                            flexDirection: 'row',
                            padding: 1,
                            backgroundColor: '#f0f0f0',
                            borderRadius: 1,
                            height: 56,
                          }}
                        >
                          <Box sx={{ display: 'flex' }}>
                            <Radio size="small" />
                          </Box>
                          <Box sx={{ display: 'flex', paddingLeft: 1 }}>
                            <AddCardIcon />
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              flex: 1,
                              paddingLeft: 2,
                              paddingRight: 2,
                              alignItems: 'center',
                            }}
                          >
                            <Typography variant="subtitle2">Add new payment method</Typography>
                          </Box>
                        </ButtonBase>
                      </>
                    )}
                  </Box>
                )
              }}
            />
            <Box
              sx={{
                marginTop: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {totalCostOfSubscription && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                  <InfoOutlinedIcon sx={{ fontSize: '26px' }} />
                  <Typography variant="body2" component={'span'}>
                    {remainingAmountToPay && remainingAmountToPay !== totalCostOfSubscription
                      ? `Pay ${remainingAmountToPay} now, then a maximum of ${totalCostOfSubscription} per month starting from next month.`
                      : remainingAmountToPay === null
                        ? `Pay nothing now, then a maximum of ${totalCostOfSubscription} per month starting from next month.`
                        : `Subscribe for a maximum of ${totalCostOfSubscription} per month. Any unspent credits will roll over each month.`}
                    {canUsePromotionalCode &&
                      !!appliedPromotionalCode &&
                      promotionalCredits !== null && (
                        <Typography
                          variant="body2"
                          component={'span'}
                          sx={{
                            color: '#0b8f72',
                            backgroundColor: '#ecfaf6',
                            borderRadius: '4px',
                            padding: '1px 4px',
                          }}
                        >
                          {getPromotionalCodeBenefitsMessage({
                            promotionalCode: appliedPromotionalCode,
                            promotionalCredits,
                            monthlyCreditLimit: monthlyCreditLimitInForm || 0,
                          })}
                        </Typography>
                      )}
                  </Typography>
                </Box>
              )}
              <Button
                id="subscription-subscribe"
                onClick={onHandleSubmitSubscribe}
                disabled={subscribeDisabled}
                fullWidth
                variant="contained"
                loading={subscribeSubmitting}
                loadingPosition="end"
              >
                subscribe
              </Button>
            </Box>
            {error && (
              <Box sx={{ marginTop: 1 }}>
                <Typography variant="body2" color={'error'}>
                  {error}
                </Typography>
              </Box>
            )}
            {(config.local || config.dev) && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, marginTop: 4 }}>
                <Typography variant={'body2'}>
                  4000 0025 0000 3155 SCA with push notification etc
                </Typography>
                <Typography variant={'body2'}>4000 0000 0000 3063 3DS</Typography>
                <Typography variant={'body2'}>4242 4242 4242 4242 success</Typography>
                <Typography variant={'body2'}>4000 0000 0000 0341 fail</Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  )
}

export default memo(SubscriptionPanel)
